import { useEffect, useState, type ReactNode } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useNavigationType,
} from "react-router-dom";

import { api } from "@/lib/api";
import { isLoggedIn, useSession } from "@/lib/auth";
import { clockTime } from "@/lib/format";
import {
  onForegroundMessage,
  pushStatus,
  registerForPush,
  type PushStatus,
} from "@/lib/push";
import { useInstallPrompt, useOnline } from "@/lib/pwa";
import { RefreshProvider, useRefreshTarget } from "@/lib/refresh";
import { DetailScreen } from "@/screens/DetailScreen";
import { LoginScreen } from "@/screens/LoginScreen";
import { SignalsScreen } from "@/screens/SignalsScreen";
import { TickersScreen } from "@/screens/TickersScreen";

const TABS = [
  { to: "/", icon: "📊", label: "신호", title: "신호" },
  { to: "/tickers", icon: "⚙️", label: "종목", title: "종목 관리" },
];

const TITLES: Record<string, string> = { "/login": "관리자 로그인" };

/**
 * 관리자 전용 화면 가드.
 *
 * 서버가 이미 관리자 API 를 막고 있으므로 이 가드는 보안장치가 아니라, 로그인 없이
 * 못 쓰는 화면 앞에서 헛수고하지 않게 하는 안내다. 원래 목적지를 state 로 넘겨
 * 로그인 후 되돌아온다.
 */
function RequireAdmin({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();

  if (!session) {
    return (
      <Navigate to="/login" replace state={{ from: location.pathname }} />
    );
  }
  return <>{children}</>;
}

function Shell() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const detail = useMatch("/stock/:symbol");

  const online = useOnline();
  const target = useRefreshTarget();
  const { canInstall, promptInstall } = useInstallPrompt();

  const session = useSession();
  const [push, setPush] = useState<PushStatus>(() => pushStatus());
  const [polling, setPolling] = useState(false);
  const [toast, setToast] = useState<{ title: string; body: string } | null>(null);

  // 이미 허용된 경우에만 조용히 토큰을 재등록한다(권한 창을 띄우지 않음).
  useEffect(() => {
    if (pushStatus() === "granted") void registerForPush();
  }, []);

  // 저장된 세션이 서버에서도 아직 유효한지 부팅 때 한 번 확인한다.
  // 로컬 만료 시각만 믿으면 비밀번호가 바뀐 뒤에도 로그인된 것처럼 보인다.
  // 401 이면 api 계층이 세션을 지우고, 네트워크 오류면 그대로 둔다(오프라인 보호).
  useEffect(() => {
    if (!isLoggedIn()) return;
    const controller = new AbortController();
    void api.me(controller.signal).catch(() => {
      // 처리는 api 계층(401 → 세션 폐기)이 담당한다.
    });
    return () => controller.abort();
  }, []);

  // 앱이 열려 있는 동안 도착한 알림은 토스트로 표시(백그라운드는 서비스워커가 처리).
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void onForegroundMessage((message) => setToast(message)).then((fn) => {
      if (cancelled) fn();
      else unsubscribe = fn;
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 6_000);
    return () => window.clearTimeout(id);
  }, [toast]);

  // 뒤로가기(POP)는 브라우저의 스크롤 복원에 맡기고, 새 이동만 최상단으로.
  useEffect(() => {
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  const symbol = detail?.params.symbol?.toUpperCase() ?? null;
  const title =
    symbol ??
    TABS.find((t) => t.to === location.pathname)?.title ??
    TITLES[location.pathname] ??
    "Unearth";

  useEffect(() => {
    document.title = `${title} · Unearth`;
  }, [title]);

  const enableNotifications = async () => {
    await registerForPush();
    setPush(pushStatus());
  };

  const logout = () => {
    api.logout();
    // 관리자 전용 화면에 있었다면 가드가 곧바로 /login 으로 보낸다.
    navigate("/", { replace: true });
  };

  // 서버가 KIS 에서 시세를 다시 받게 한다. 헤더의 ↻(화면 재조회)와는 다르다.
  // 외부 API 를 종목 수만큼 호출하므로 수 초~십수 초가 걸린다 → 진행 상태를 보여준다.
  const refreshQuotes = async () => {
    if (polling) return;
    setPolling(true);
    try {
      const res = await api.refreshQuotes();
      setToast({ title: "시세 갱신 완료", body: res.message ?? "" });
      target?.refresh(); // 갱신된 값으로 현재 화면을 다시 읽는다.
    } catch (e) {
      setToast({
        title: "시세 갱신 실패",
        body: e instanceof Error ? e.message : "알 수 없는 오류",
      });
    } finally {
      setPolling(false);
    }
  };

  return (
    <div className="app">
      <div className="topbar">
        {!online && (
          <div className="banner" role="status">
            오프라인 — 마지막으로 받은 데이터를 표시합니다
          </div>
        )}

        <header className="header">
          {symbol ? (
            <button className="back-btn" type="button" onClick={() => navigate(-1)}>
              ‹ 뒤로
            </button>
          ) : (
            <Link className="brand" to="/">
              Unearth
            </Link>
          )}

          <span className="header-title">{title}</span>

          <div className="header-actions">
            {target?.updatedAt != null && (
              <span className="updated-at" title="마지막 갱신 시각">
                {clockTime(target.updatedAt)}
              </span>
            )}

            {canInstall && (
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => void promptInstall()}
              >
                설치
              </button>
            )}

            {push === "default" && (
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => void enableNotifications()}
              >
                알림 켜기
              </button>
            )}

            {session && (
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => void refreshQuotes()}
                disabled={polling}
                title="서버가 KIS 에서 시세를 다시 받아 신호를 재계산합니다"
              >
                {polling ? "갱신 중…" : "시세 갱신"}
              </button>
            )}

            {session && (
              <button
                className="btn btn--ghost"
                type="button"
                onClick={logout}
                title={`${session.username} 로 로그인됨`}
              >
                로그아웃
              </button>
            )}

            <button
              className={target?.refreshing ? "icon-btn icon-btn--spin" : "icon-btn"}
              type="button"
              onClick={() => target?.refresh()}
              disabled={!target || target.refreshing}
              aria-label="화면 새로고침"
              title="화면 새로고침(서버에 저장된 값을 다시 읽습니다)"
            >
              ↻
            </button>
          </div>
        </header>

        <nav className="tabs">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end
              className={({ isActive }) => (isActive ? "tab active" : "tab")}
            >
              <span className="tab-icon" aria-hidden="true">
                {t.icon}
              </span>
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="main">
        <Routes>
          <Route path="/" element={<SignalsScreen />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route
            path="/tickers"
            element={
              <RequireAdmin>
                <TickersScreen />
              </RequireAdmin>
            }
          />
          <Route path="/stock/:symbol" element={<DetailScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {toast && (
        <div className="toast" role="status">
          <div className="toast-title">{toast.title}</div>
          {toast.body && <div className="toast-body">{toast.body}</div>}
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <RefreshProvider>
        <Shell />
      </RefreshProvider>
    </BrowserRouter>
  );
}
